import { useMemo } from 'react';
import { computePeriodMemo } from '@/metrics/engine';
import { addDays, daysBetweenInclusive, eachDay, osloDateOf } from '@/domain/time';
import { DEMO_WINDOW } from '@/fixtures/scenarios';

/** Grafvindu: valgt periode hvis >= 7 dager, ellers 14 dager som slutter på valgt sluttdato. */
export function chartWindow(fromDate, toDate, minDays = 14) {
  const len = daysBetweenInclusive(fromDate, toDate);
  if (len >= 7) return { fromDate, toDate };
  let start = addDays(toDate, -(minDays - 1));
  if (start < DEMO_WINDOW.from) start = DEMO_WINDOW.from;
  return { fromDate: start, toDate };
}

export function useChartDays(dataset, query) {
  return useMemo(() => {
    if (!dataset) return [];
    const w = chartWindow(query.fromDate, query.toDate);
    const res = computePeriodMemo(dataset, { ...query, fromDate: w.fromDate, toDate: w.toDate });
    return res.days;
  }, [dataset, query]);
}

export function useMachineResult(dataset, query, machineId) {
  return useMemo(() => {
    if (!dataset || !machineId) return null;
    return computePeriodMemo(dataset, { ...query, machineIds: [machineId] });
  }, [dataset, query, machineId]);
}

export function useMachineChartDays(dataset, query, machineId) {
  return useMemo(() => {
    if (!dataset || !machineId) return [];
    const w = chartWindow(query.fromDate, query.toDate);
    return computePeriodMemo(dataset, { ...query, machineIds: [machineId], fromDate: w.fromDate, toDate: w.toDate }).days;
  }, [dataset, query, machineId]);
}

/**
 * Små trendserier per maskin for grafvinduet (dagsum av registrert diesel og tomgangstimer
 * direkte fra kildepostene). Brukes kun til sparklines i tabeller – ikke til fasittall.
 * Dager uten målinger gir null (bryter linjen). Dager med manglende dieselfelt markeres som ufullstendige.
 */
export function useMachineSparklines(dataset, query) {
  return useMemo(() => {
    if (!dataset) return { days: [], byMachine: {} };
    const w = chartWindow(query.fromDate, query.toDate);
    const days = eachDay(w.fromDate, w.toDate);
    const index = new Map(days.map((d, i) => [d, i]));
    const byMachine = {};
    for (const mi of dataset.machineIntervals) {
      if (mi.quality === 'quarantined') continue;
      const d = osloDateOf(mi.fromUtc);
      const i = index.get(d);
      if (i === undefined) continue;
      let rec = byMachine[mi.machineId];
      if (!rec) {
        rec = { fuel: days.map(() => null), idle: days.map(() => null), incomplete: days.map(() => false) };
        byMachine[mi.machineId] = rec;
      }
      if (mi.fuelLitres === null) rec.incomplete[i] = true;
      else rec.fuel[i] = (rec.fuel[i] ?? 0) + mi.fuelLitres;
      if (mi.idleHours !== null) rec.idle[i] = (rec.idle[i] ?? 0) + mi.idleHours;
    }
    return { days, byMachine };
  }, [dataset, query]);
}
